import { useMemo } from 'react';
import { cn } from '@/lib/core/utils/cn';
import { useClientTranslation } from '@/lib/core/i18n';
import type { TranslationKey } from '@/lib/core/i18n/types';
import { SpinLoading } from '@/components/shared/loadings/spin';
import type { MewsHourSlot } from '@/services/platform/data/booking/mews';

export interface ExperienceHourSlotsProps {
  slots: MewsHourSlot[];
  /** `startEpochMs` of the currently picked slot, or null. See `MewsHourSlot.startEpochMs`. */
  selectedEpochMs: number | null;
  isLoading: boolean;
  onSelect: (slot: MewsHourSlot) => void;
}

/**
 * The parts of the day the grid is split into.
 *
 * SevenRooms hands its own shifts down (`ShiftAvailabilityDto.name`), but Mews has no equivalent —
 * `GetConfiguration` returns a flat run of time units — so the boundaries are stated here. Compared
 * against `MewsHourSlot.hour`, which is UTC, exactly like the `label` printed on the button: both
 * come off the same instant, so a slot always lands under the heading its own label agrees with.
 */
const SLOT_GROUPS: { labelKey: TranslationKey; includes: (hour: number) => boolean }[] = [
  { labelKey: 'App.Labels.SlotGroupMorning', includes: hour => hour < 12 },
  { labelKey: 'App.Labels.SlotGroupAfternoon', includes: hour => hour >= 12 && hour < 18 },
  { labelKey: 'App.Labels.SlotGroupEvening', includes: hour => hour >= 18 },
];

/**
 * Hour grid for a Mews `Hour` service, e.g. Massages.
 *
 * Deliberately separate from `BookShiftTimeSlots`, which renders the same kind of grid for
 * SevenRooms: that component's props are SevenRooms DTOs (`ShiftAvailabilityDto`, `SeatingAreaDto`)
 * and it also owns the seating-area picker, so driving it with Mews data would mean fabricating
 * SevenRooms shapes. The button states and the banner below intentionally match it visually.
 */
export function ExperienceHourSlots({
  slots,
  selectedEpochMs,
  isLoading,
  onSelect,
}: ExperienceHourSlotsProps) {
  const t = useClientTranslation();

  // Empty parts of the day are dropped rather than rendered as a bare heading — a service that only
  // runs mornings should not show an "Evening" label with nothing under it.
  const groups = useMemo(
    () =>
      SLOT_GROUPS.map(group => ({
        labelKey: group.labelKey,
        slots: slots.filter(slot => group.includes(slot.hour)),
      })).filter(group => group.slots.length > 0),
    [slots]
  );

  if (isLoading) return <SpinLoading wrapperClassName="min-h-0 py-8" />;

  if (slots.length === 0) {
    return (
      <p className="font-pp-neue-montreal px-4 py-8 text-center text-[14px] tracking-[0.5px] text-[#707070]">
        {t('App.Labels.SlotFullyBooked')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="mx-4 flex items-center gap-4 bg-[#f4f4f4] px-4 py-2">
        <div className="flex flex-1 flex-col gap-0.5">
          <p className="font-pp-neue-montreal text-[12px] font-semibold tracking-[0.5px] text-[#1a1a1a]">
            {t('App.Labels.SlotNotAvailable')}
          </p>
          <p className="font-pp-neue-montreal text-[12px] font-normal tracking-[0.5px] text-[#666]">
            {t('App.Labels.SlotNotAvailableDescription')}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-6 px-4">
        {groups.map(group => (
          <div key={group.labelKey} className="flex flex-col gap-4">
            <p className="font-pp-neue-montreal text-[16px] font-medium tracking-[0.25px] text-[#1a1a1a]">
              {t(group.labelKey)}
            </p>
            <div className="grid grid-cols-4 gap-2">
              {group.slots.map(slot => {
                const unavailable = !slot.isAvailable;
                return (
                  <button
                    key={slot.startEpochMs}
                    type="button"
                    disabled={unavailable}
                    onClick={() => onSelect(slot)}
                    className={cn(
                      'font-pp-neue-montreal w-full rounded-[2px] px-2 py-4 text-center text-[14px] tracking-[0.5px] transition-colors',
                      selectedEpochMs === slot.startEpochMs && !unavailable
                        ? 'bg-[#1a1a1a] text-white outline-[0.5px] outline-[#1a1a1a]'
                        : unavailable
                          ? 'text-[#bababa] outline-[0.5px] outline-[#eaeaea]'
                          : 'cursor-pointer text-[#4e4e4e] outline-[0.5px] outline-[#eaeaea]'
                    )}
                  >
                    {slot.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
